/**
 * Location health (docs/LOCATION_HEALTH.md). Owner, 2026-09-29: "Dashboard of the Overall health of a location across
 * all cleanliness vectors of all rooms, surfaces and objects in those rooms. The last time it was inspected."
 *
 * Nothing here invents state. Every line comes from records other modules already judge:
 *   schedule.ts expectedChecks / satisfiedBy / isLate (due, late, missed; closed days and first days expect nothing late),
 *   log entries and their evaluation and fix (templates.ts), objects.ts objectState (each Places object's interval),
 *   roomcheck.ts room checks (Clear ✓, items found wrong and not fixed), wholeplace.ts "Ready to open ✓",
 *   foodstore.ts throwOutFlag (F10), open reports (never private or health reports), supplies (out, below par),
 *   basis.ts (law / OSHA / maker / suggested).
 *
 * Gates (test/locationhealth.test.ts):
 * | H1 Statuses | each item is one of: failed (found wrong, not fixed) > late > never checked > due soon > OK |
 * | H2 Worst wins | a room and a vector show their worst item; a failed item beats any number of OK ones |
 * | H3 Own records | each vector reads only the records listed in HEALTH_VECTORS[].reads; adding another vector's record never changes it |
 * | H4 Worst first | the overall line lists what needs you (failed, then late), grave first, then law before suggested |
 * | H5 No mystery score | no blended score; a vector shows a number only with its formula ("7 of 8 checks on time today") |
 * | H6 Closed days | a closed day expects no checks and makes no object late (it shows "Due when you open") |
 * | H7 Who and when | every item carries its last check (time, first name) when a record has one; rooms carry "last inspected" |
 */
import type { Credential, Id, Incident, IsoDate, IsoDateTime, LogEntry, PermitType } from "./model.ts";
import { LOCATIONHEALTH_UT } from "./states/ut/locationhealth.ts";
import { perStateTable } from "./states/access.ts";
import type { StateWords, UsState } from "./states/types.ts";
import { wordsFor } from "./states/words.ts";

const WORDS: Record<UsState, StateWords<typeof LOCATIONHEALTH_UT>> = perStateTable("LOCATIONHEALTH", LOCATIONHEALTH_UT);
const words = (state?: UsState) => wordsFor(WORDS, state);
import { formReadiness, type FormReadiness } from "./inspectionform.ts";
import { inspectorFormFor, inspectorView, picFact, type InspectorView } from "./inspector.ts";
import type { ActivityEvent, PlaceObject } from "./objects.ts";
import { objectNeeds, objectState, type ZonePreset } from "./objects.ts";
import type { DayContext, ExpectedCheck } from "./schedule.ts";
import { dayMinutes, expectedChecks, isLate, satisfiedBy } from "./schedule.ts";
import type { Supply } from "./actions.ts";
import { firstName } from "./actions.ts";
import { counts, type Correction } from "./corrections.ts";
import { getSurface } from "./surfaces.ts";
import { getTemplate } from "./templates.ts";
import { incidentInfo } from "./incidents.ts";
import { URGENT_RULES, isPrivateReport } from "./nag.ts";
import { HEALTH_INCIDENT_TYPES, MIN_EVENTS, RESTROOM_SUPPLIES, addDays as addDaysIso, checkOutcomes } from "./analytics.ts";
import { GRAVE_REPORT_TYPES, GRAVE_WORDS, SOON_MIN, TEMPLATE_SURFACE, roomMap, supplyBasis } from "./roomboard.ts";
import { roomKeyOf, roomRunView, type ItemClass, type RoomRun } from "./roomcheck.ts";
import type { PlaceRun } from "./wholeplace.ts";
import { throwOutFlag, type FoodRecord } from "./foodstore.ts";
import { localDateMinutes } from "./hours.ts";
import { seedForTask } from "./tasks.ts";
import { BASIS_RANK, basisOfCitations, basisOfRequirements, basisOfRule, basisOfSurface, basisOfTask, type Basis, type BasisInfo } from "./basis.ts";

/* ─────────────── statuses ─────────────── */

export type HealthStatus = "failed" | "late" | "never" | "soon" | "ok";
/** Worst first (H1). */
export const HEALTH_ORDER: readonly HealthStatus[] = ["failed", "late", "never", "soon", "ok"];
export const HEALTH_RANK: Record<HealthStatus, number> = { failed: 0, late: 1, never: 2, soon: 3, ok: 4 };
/** The word beside the color (never color alone). */
export const HEALTH_WORD: Record<HealthStatus, string> = { failed: "Needs a fix", late: "Late", never: "Never checked", soon: "Due soon", ok: "OK" };
/** The color token each status uses (the apps' stop / warn / info / go chips). */
export const HEALTH_TONE: Record<HealthStatus, "stop" | "warn" | "info" | "go"> = { failed: "stop", late: "warn", never: "info", soon: "info", ok: "go" };

/* ─────────────── vectors ─────────────── */

export type VectorKey = "food" | "surfaces" | "restrooms" | "pests" | "trash_floors" | "supplies" | "other";

export interface VectorDef {
  key: VectorKey;
  label: string;
  /** A home's label, when it differs. */
  homeLabel?: string;
  /** The records this vector reads, in plain words (shown under "How we know"). Nothing else changes it (H3). */
  reads: string[];
  /** Where the obligation comes from (a home is always Suggested). */
  citations: string[];
  reason?: string;
  /** Scopes that have records for it. */
  scopes: readonly ("business" | "home")[];
  /** Shown only when it has something (the catch-all). */
  onlyWhenUsed?: true;
}

/**
 * The vectors. Each reads its own records; an item belongs to exactly one. Log templates by vector are in
 * TEMPLATE_VECTOR; Places objects and room-check items by their surface or class (vectorOfSurface, vectorOfClass).
 */
export const HEALTH_VECTORS: readonly VectorDef[] = [
  {
    key: "food", label: "Food safety", homeLabel: "Food",
    reads: ["Fridge, freezer, cooking, holding, cooling, receiving and thermometer logs", "Food past its date or out too long (Food screen)", "Open reports: temperature, contamination, water or power out"],
    citations: ["Food Code 3-501.16", "Food Code 3-501.17"], scopes: ["business", "home"],
  },
  {
    key: "surfaces", label: "Surfaces & sanitizing", homeLabel: "Surfaces",
    reads: ["Sanitizer and cleaning sign-off logs", "Cleaning items that aren't restroom, trash or floor", "Room checks: counters, tables, tools and other surfaces", "Open reports: chemical spill, vomit cleanup"],
    citations: ["Food Code 4-602.11", "Food Code 4-501.114"], scopes: ["business", "home"],
  },
  {
    key: "restrooms", label: "Restrooms & handwashing", homeLabel: "Bathroom & handwashing",
    reads: ["Restroom checks", "Restroom and hand-sink objects in Cleaning", "Room checks: sinks and toilets"],
    citations: ["Food Code 6-501.18", "Food Code 6-301.11"], scopes: ["business", "home"],
  },
  {
    key: "pests", label: "Pests",
    reads: ["Quick pest looks and the weekly walk-through", "Open pest reports"],
    citations: ["Food Code 6-501.111"], scopes: ["business"],
  },
  {
    key: "trash_floors", label: "Trash & floors",
    reads: ["Trash, floor and drain objects in Cleaning", "Room checks: trash and floors"],
    citations: ["Food Code 5-501.116", "OSHA 29 CFR 1910.22(a)(2)"], scopes: ["business", "home"],
  },
  {
    key: "supplies", label: "Supplies",
    reads: ["Supply counts and \"Out of it\" (Settings → Supplies, Order)"],
    citations: [], reason: "No single rule: each supply shows its own (soap and towels are Food Code 6-301.11–12).", scopes: ["business"],
  },
  {
    key: "other", label: "Other checks",
    reads: ["Checklist tasks with no room surface", "Other open reports (equipment, something else)"],
    citations: [], reason: "Your own checklist and reports.", scopes: ["business"], onlyWhenUsed: true,
  },
];

/** A home is never law or OSHA (basis.ts). */
const HOME_BASIS: BasisInfo = basisOfCitations([], { reason: "A home: suggested, never a law." });
export function vectorBasis(v: VectorDef, scope: "business" | "home"): BasisInfo {
  return scope === "home" ? HOME_BASIS : basisOfCitations(v.citations, v.reason ? { reason: v.reason } : {});
}

/** Log template → vector (H3). task_done goes by the task's surface; anything else not listed is "other". */
export const TEMPLATE_VECTOR: Record<string, VectorKey> = {
  cold_units: "food", transport_temp: "food", returned_food: "food", advisory_check: "food", egg_check: "food", special_process: "food", tcs_items: "food", temp_cook_hold: "food", cooling: "food", receiving: "food", calibration: "food", time_control: "food", discard: "food", commissary_log: "food", storage_order: "food", prep_switch: "food", produce_wash: "food", ice_check: "food", backflow_test: "surfaces",
  water_temp: "surfaces",
  sanitizer: "surfaces", cleaning_signoff: "surfaces",
  restroom_check: "restrooms",
  pest_check: "pests", pest_sightings: "pests",
};
/** Short names for a log's line ("Restroom not checked since 9:10 AM"). */
export const TEMPLATE_SUBJECT: Record<string, string> = {
  cold_units: "Fridge and freezer temps", tcs_items: "TCS food temps", temp_cook_hold: "Cooking and holding temps", cooling: "Cooling", receiving: "Receiving",
  calibration: "Thermometer check", time_control: "Time-control food", discard: "Discard log", storage_order: "Fridge order", dry_storage: "Dry storage check", transport_temp: "Food on the road", returned_food: "Returned or leftover food", advisory_check: "Menu advisory", egg_check: "Pasteurized eggs", special_process: "Special process list", prep_switch: "Raw to ready-to-eat switch", produce_wash: "Produce wash", ice_check: "Ice check", ready_to_work: "Ready-to-work check", taste_drink: "Tasting, sipping and break check", utensil_rest: "In-use utensil check", single_service: "Single-service check", backflow_test: "Backflow test", water_temp: "Water temperature", injury_response: "Cut or burn", fire_check: "Fire safety check", commissary_log: "Commissary log", sanitizer: "Sanitizer",
  cleaning_signoff: "Cleaning sign-off", restroom_check: "Restroom", pest_check: "Pest checks", pest_sightings: "Pest sightings",
};
/** Reports by type → vector. Private and health reports never come in (analytics A4, nag.ts isPrivateReport). */
export const REPORT_VECTOR: Partial<Record<Incident["type"], VectorKey>> = {
  pest: "pests", temperature: "food", contamination: "food", water_power_outage: "food", chemical_spill: "surfaces", vomit_diarrhea_cleanup: "surfaces",
};

export function vectorOfSurface(surfaceId: string | undefined): VectorKey {
  const s = surfaceId ? getSurface(surfaceId) : undefined;
  if (!s) return "surfaces";
  if (s.area === "restroom" || s.kind === "handwashing") return "restrooms";
  if (/trash|dumpster|floor|drain|mop|grease-trap/.test(s.id)) return "trash_floors";
  return "surfaces";
}
/** A surface's tags for the inspection form map. */
export function surfaceTags(surfaceId: string | undefined): string[] {
  const s = surfaceId ? getSurface(surfaceId) : undefined;
  if (!s) return surfaceId ? [`surface:${surfaceId}`] : [];
  return [`surface:${s.id}`, `area:${s.area}`, `kind:${s.kind}`, ...(/trash|dumpster/.test(s.id) ? ["trash"] : []), ...(/floor|drain|wall/.test(s.id) ? ["floor"] : [])];
}
export function vectorOfClass(cls: ItemClass, height?: string): VectorKey {
  if (cls === "hand_sink" || cls === "toilet_zone") return "restrooms";
  if (cls === "trash" || height === "floor") return "trash_floors";
  if (cls === "food") return "food";
  return "surfaces";
}

/* ─────────────── inspection visits ─────────────── */

export type InspectionResult = "passed" | "passed_with_fixes" | "failed";
export const INSPECTION_RESULTS: readonly { key: InspectionResult; label: string }[] = [
  { key: "passed", label: "Passed" },
  { key: "passed_with_fixes", label: "Passed, with things to fix" },
  { key: "failed", label: "Didn't pass" },
];
/** The last health department inspection, as the owner or a manager entered it (Location.lastInspection, migration 0029). */
export interface HealthInspection {
  /** The inspection's date (local). */
  on: IsoDate;
  result: InspectionResult;
  note?: string;
  /** Who entered it, and when (server time). */
  by: Id;
  byName?: string;
  at: IsoDateTime;
}
/** Checks a request body for the inspection write: `{ on, result, note? }`. */
export function parseInspection(b: Record<string, unknown>, today: IsoDate): { on: IsoDate; result: InspectionResult; note?: string } | { error: string } {
  const on = typeof b.on === "string" ? b.on : "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(on) || Number.isNaN(Date.parse(`${on}T12:00:00Z`))) return { error: 'Pick the date of the inspection ("on", like 2026-09-01).' };
  if (on > today) return { error: "The inspection date can't be in the future." };
  if (on < "2000-01-01") return { error: "Pick a date after 2000." };
  const result = INSPECTION_RESULTS.find((r) => r.key === b.result)?.key;
  if (!result) return { error: 'Pick how it went ("result": passed, passed_with_fixes or failed).' };
  if (b.note !== undefined && b.note !== null && typeof b.note !== "string") return { error: '"note" must be text.' };
  const note = typeof b.note === "string" ? b.note.trim().slice(0, 300) : "";
  return { on, result, ...(note ? { note } : {}) };
}

/* ─────────────── the view ─────────────── */

export type HealthActionKind = "log" | "fix" | "object" | "report" | "food" | "order" | "room";
export interface HealthAction {
  kind: HealthActionKind;
  /** Verb + object: "Log it now", "Add what you did", "Clean it", "Open the report", "Open the food", "Order it", "Check this room". */
  label: string;
  /** check: an expected check's key; template: a log with nothing expected today; task, entry, object, incident, food, supply, room. */
  target: { type: "check" | "template" | "task" | "entry" | "object" | "incident" | "food" | "supply" | "room"; id: string };
  templateId?: string;
}

export interface HealthItem {
  /** Stable: log:<template or task>, object:<id>, incident:<id>, food:<id>, food:store, supply:<key>, roomitem:<run>:<item>. */
  id: string;
  vector: VectorKey;
  room: string;
  name: string;
  status: HealthStatus;
  word: string;
  /** "Walk-in over 41°F at 9:10 AM", "Missed at 11:00 AM · last checked 7:05 AM", "Checked 9:10 AM · Sam". */
  line: string;
  /** For the overall line: "Walk-in over 41°F", "Restroom not checked since 9:10 AM". */
  short: string;
  /** An imminent hazard or a food-safety limit broken now (nag.ts URGENT_RULES, roomboard grave reports). */
  grave: boolean;
  basis: Basis;
  basisLine: string;
  lastCheckedAt?: IsoDateTime;
  lastCheckedBy?: string;
  dueAt?: IsoDateTime;
  /** A failed item: when it was found (the record, the report, the date it went bad, the room check answer). */
  since?: IsoDateTime;
  action: HealthAction;
  /** What the item is, for the inspection form map (inspectionform.ts): tpl:<log>, surface:<id>, area:<area>, kind:<kind>, class:<class>, req:<id>, report:<type>, supply:<key>, food, food:throw, trash, floor, sewage. */
  tags: string[];
}

export interface HealthScore {
  done: number;
  of: number;
  pct: number;
  /** Always shown with the number (H5): "7 of 8 checks on time today". */
  formula: string;
}

export interface HealthCounts { failed: number; late: number; never: number; soon: number; ok: number }

export interface TrendWindow { done: number; of: number; pct: number; days: number }
export interface HealthTrend {
  last30: TrendWindow | null;
  last7: TrendWindow | null;
  /** "On time: 92% over 30 days → 97% over the last 7". */
  line: string;
  /** Always shown with the numbers. */
  formula: string;
}

export interface HealthVector {
  key: VectorKey;
  label: string;
  status: HealthStatus;
  word: string;
  tone: "stop" | "warn" | "info" | "go";
  /** "2 need a fix", "All OK · last checked 9:10 AM", "Nothing recorded yet". */
  line: string;
  basis: Basis;
  basisLine: string;
  reads: string[];
  counts: HealthCounts;
  score?: HealthScore;
  /** On time over the last 30 and 7 days before today (analytics.ts checkOutcomes), only with enough checks (MIN_EVENTS). */
  trend?: HealthTrend;
  /** Rooms this vector touches, worst first. */
  rooms: { name: string; status: HealthStatus; word: string; count: number }[];
  /** Worst first. */
  items: HealthItem[];
}

export interface HealthRoom {
  key: string;
  name: string;
  status: HealthStatus;
  word: string;
  tone: "stop" | "warn" | "info" | "go";
  line: string;
  counts: HealthCounts;
  lastCheckedAt?: IsoDateTime;
  lastCheckedBy?: string;
  /** The last Clear ✓ of a room check (or "Ready to open ✓" of a whole-place clean). */
  lastInspectedAt?: IsoDateTime;
  lastInspectedBy?: string;
  /** "Last inspected: 2:14 PM · Sam" or "Never inspected". */
  inspectedLine: string;
  items: HealthItem[];
}

export interface LocationHealth {
  locationId: Id;
  scope: "business" | "home";
  name: string;
  at: IsoDateTime;
  date: IsoDate;
  status: HealthStatus;
  word: string;
  tone: "stop" | "warn" | "info" | "go";
  /** The one glance line: "2 things need you: Walk-in over 41°F · Restroom not checked since 9:10 AM". */
  line: string;
  /** The items in the line, worst first (failed, then late). */
  needs: HealthItem[];
  counts: HealthCounts;
  vectors: HealthVector[];
  rooms: HealthRoom[];
  /** The latest room check Clear ✓ or whole-place Ready anywhere in the place. */
  lastInspected: { at: IsoDateTime; by?: string; what: string; line: string } | null;
  /** The last health department inspection (business only). */
  inspection: (HealthInspection & { resultLabel: string; line: string }) | null;
  inspectionLine: string;
  canEditInspection: boolean;
  /** "Next inspection: we don't know. Utah counties set it." (or the sourced frequency for a food truck). Business only. */
  nextInspectionLine: string;
  /** Get inspection-ready: what to put right first (the items that need you, then law items never checked). */
  ready: { line: string; itemIds: string[] };
  /** The FDA model inspection form, item by item: what's on file (inspectionform.ts). Business only. */
  form: FormReadiness | null;
  /** The place's own inspection form, in its order, with what's on file for each item (inspector.ts). Null = not mapped here, or a home. */
  inspector: InspectorView | null;
  closedToday: boolean;
}

type EntryIn = Pick<LogEntry, "id" | "templateId" | "createdAt" | "evaluation" | "values"> & { taskId?: Id; createdBy?: Id; correctiveAction?: string; correction?: Correction };

export interface HealthInput {
  scope: "business" | "home";
  now: Date | IsoDateTime;
  timeZone: string;
  location: { id: Id; name: string; lastInspection?: HealthInspection; permitType?: PermitType; state?: UsState; /** Picks the health authority's own form (inspector.ts). */ county?: string | null; /** Location.seats (inspector.ts: a certificate tied to seating). */ seats?: number };
  /** The team's credentials (kind and expiry): evidence on the inspector's form (inspector.ts). Absent = not loaded. */
  credentials?: readonly Pick<Credential, "kind" | "expiresOn">[];
  /** Signs marked posted (signs.ts ids) and the day: evidence on the inspector's form. */
  postedSigns?: readonly { id: string; date: IsoDate }[];
  /** Business: the zone preset (objects.ts) and the location's areas, for putting checks in rooms. */
  preset?: ZonePreset;
  areas?: string[];
  /** Business: today (api day.ts buildDay). Its entries are today's records. */
  day?: DayContext;
  /** Records over the last HISTORY_DAYS with who wrote them (for "last checked"); today's included. */
  entries?: EntryIn[];
  objects?: PlaceObject[];
  events?: (Pick<ActivityEvent, "objectId" | "kind" | "at"> & { by?: string; correction?: Correction })[];
  learnedHours?: Record<Id, number>;
  reports?: Pick<Incident, "id" | "type" | "area" | "title" | "details" | "status" | "reportedAt" | "reportedBy">[];
  food?: (FoodRecord & { updatedAt?: string; updatedBy?: string })[];
  supplies?: Supply[];
  /** The place's rooms (roomcheck.ts roomsFor). */
  rooms: { key: string; name: string }[];
  roomRuns?: RoomRun[];
  placeRuns?: Pick<PlaceRun, "readyAt" | "readyBy" | "readyByName" | "closedAt">[];
  /** User id → name, for who checked. */
  names?: Record<Id, string>;
  canEditInspection?: boolean;
  /**
   * Trends: the checks the schedule expected on each of the last TREND_DAYS days before today (analytics.ts
   * analyticsExpectedChecks) and the records of those days. Absent: no trends.
   */
  past?: { expected: Pick<ExpectedCheck, "templateId" | "taskId" | "dueAt" | "lateAt">[]; entries: Pick<LogEntry, "id" | "templateId" | "taskId" | "createdAt" | "evaluation" | "correctiveAction">[] };
}

/** The overall line on a place's first day, when nothing is late: no count of things never checked. */
export const FIRST_DAY_LINE = "Your first day. Nothing is late. Start with the first check.";

/** How far back "last checked" and trends look (the API loads this many days of records). */
export const HISTORY_DAYS = 30;
export const TREND_DAYS = 30;
export const TREND_FORMULA_BASE = "Checks written by the end of their grace time ÷ checks due, on days before today.";
/** A failed item not fixed this long after it was found alerts the person in charge. ASSUMED: no source sets a window. */
export const ALERT_AFTER_MIN = 30;
/** Food trucks: at least one inspection per permit year (R392-102-16(10)(b); docs/source/research/food-truck-utah.md). */
export const nextInspectionTruckFor = (state?: UsState): string | null => words(state).nextInspectionTruck;
export const nextInspectionUnknownFor = (state?: UsState): string => ["Next inspection: we don't know.", words(state).nextInspectionCountySets].filter(Boolean).join(" ");
export const NEXT_INSPECTION_TRUCK = nextInspectionTruckFor("UT") as string;
export const NEXT_INSPECTION_UNKNOWN = nextInspectionUnknownFor("UT");

const addMin = (iso: IsoDateTime, m: number) => new Date(Date.parse(iso) + m * 60_000).toISOString();
const lower = (s: string | undefined) => (s ?? "").trim().toLowerCase();
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const zeroCounts = (): HealthCounts => ({ failed: 0, late: 0, never: 0, soon: 0, ok: 0 });
const worst = (xs: readonly HealthStatus[], none: HealthStatus = "ok"): HealthStatus => xs.reduce<HealthStatus>((m, s) => (HEALTH_RANK[s] < HEALTH_RANK[m] ? s : m), xs.length ? "ok" : none);

/** Worst first: status, then grave, then law/OSHA before maker before suggested, then the oldest, then the name (H4). */
export function compareHealth(a: HealthItem, b: HealthItem): number {
  return HEALTH_RANK[a.status] - HEALTH_RANK[b.status] || Number(b.grave) - Number(a.grave) || BASIS_RANK[a.basis] - BASIS_RANK[b.basis]
    || (a.dueAt ?? a.lastCheckedAt ?? "~").localeCompare(b.dueAt ?? b.lastCheckedAt ?? "~") || a.name.localeCompare(b.name);
}

function countOf(items: readonly HealthItem[]): HealthCounts {
  const c = zeroCounts();
  for (const i of items) c[i.status]++;
  return c;
}

/** "Walk-in over 41°F", "Women's: no soap at the hand sink": the failed rule in a few words. */
export function failedShort(e: Pick<EntryIn, "templateId" | "values" | "evaluation">): string {
  const tpl = getTemplate(e.templateId);
  const subjectCol = tpl?.columns.find((c) => c.type === "text" && c.key !== "action" && e.values[c.key] != null && String(e.values[c.key]).trim());
  const subject = subjectCol ? String(e.values[subjectCol.key]).trim() : TEMPLATE_SUBJECT[e.templateId] ?? tpl?.title ?? "A check";
  const v = e.evaluation.violations[0];
  const rule = tpl?.rules.find((r) => r.id === v?.ruleId);
  const col = rule ? tpl?.columns.find((c) => c.key === rule.column) : undefined;
  if (rule && col?.type === "temp_f" && typeof rule.value === "number" && (rule.op === "<=" || rule.op === ">=")) return `${subject} ${rule.op === "<=" ? "over" : "under"} ${rule.value}°F`;
  if (v && URGENT_RULES[v.ruleId]) return `${subject}: ${URGENT_RULES[v.ruleId]}`;
  return `${subject}: ${(v?.message ?? "out of range").replace(/\.$/, "")}`.replace(/: (\w)/, (_, c: string) => `: ${c.toLowerCase()}`);
}

export function locationHealth(i: HealthInput): LocationHealth {
  const now = typeof i.now === "string" ? new Date(i.now) : i.now;
  const nowIso = now.toISOString();
  const soonIso = addMin(nowIso, SOON_MIN);
  const tz = i.timeZone;
  const today = i.day?.date ?? localDateMinutes(now, tz).date;
  const clock = i.day?.timeLabel ?? ((iso: IsoDateTime) => { try { return new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz }).format(new Date(iso)); } catch { return iso.slice(11, 16); } });
  const dayFmt = (() => { try { return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: tz }); } catch { return undefined; } })();
  /** Today → "9:10 AM"; another day → "Sep 27". */
  const when = (iso: IsoDateTime) => (localDateMinutes(iso, tz).date === today ? clock(iso) : dayFmt ? dayFmt.format(new Date(iso)) : iso.slice(0, 10));
  const who = (id?: string, name?: string) => { const n = name ?? (id ? i.names?.[id] : undefined); return n ? firstName(n) : undefined; };
  const checkedLine = (at?: IsoDateTime, by?: string) => (at ? `Checked ${when(at)}${by ? ` · ${by}` : ""}` : "");
  const business = i.scope === "business";
  const day = business ? i.day : undefined;
  const closedToday = !!day && !dayMinutes(day);
  const objects = business ? i.objects ?? [] : [];
  const rooms = business ? roomMap(i.preset ?? "restaurant", objects, i.areas) : undefined;
  const zoneFor = (surfaceId: string | undefined) => rooms?.zoneFor(surfaceId) ?? "Other places";
  const items: HealthItem[] = [];
  const push = (x: Omit<HealthItem, "word">) => items.push({ ...x, word: HEALTH_WORD[x.status] });
  const basisOf = (b: BasisInfo) => (business ? { basis: b.basis, basisLine: b.line } : { basis: HOME_BASIS.basis, basisLine: HOME_BASIS.line });

  /** Log template (or checklist task) → its vector: TEMPLATE_VECTOR, a task by its surface, else "other". */
  const taskById = new Map((day?.tasks ?? []).map((t) => [t.id, t]));
  const vectorOfLog = (templateId: string, taskId?: Id): VectorKey => {
    if (templateId !== "task_done") return TEMPLATE_VECTOR[templateId] ?? "other";
    const t = taskId ? taskById.get(taskId) : undefined;
    const seed = t ? seedForTask(t) : undefined;
    return seed?.surfaceId ? vectorOfSurface(seed.surfaceId) : "other";
  };

  /* Vector scores: expected checks due so far (H5). */
  const score = new Map<VectorKey, { done: number; of: number }>();

  /* 1. Logs: today's expected checks grouped by log (or task), with today's failed records and the last record. */
  if (day) {
    const dayEntries = day.entries;
    const history = (i.entries ?? []).filter(counts);
    const tasks = new Map(day.tasks.map((t) => [t.id, t as typeof t & { how?: string }]));
    const groupOf = (templateId: string, taskId?: Id) => (templateId === "task_done" && taskId ? `task:${taskId}` : `tpl:${templateId}`);
    const vectorOfGroup = vectorOfLog;
    const roomOfGroup = (templateId: string, taskId?: Id) => {
      if (templateId === "task_done" && taskId) { const t = tasks.get(taskId); return zoneFor(t ? seedForTask(t)?.surfaceId : undefined); }
      return zoneFor(TEMPLATE_SURFACE[templateId]);
    };
    const groups = new Map<string, { templateId: string; taskId?: Id; expected: ExpectedCheck[] }>();
    const addGroup = (templateId: string, taskId?: Id) => {
      if (templateId === "health_checkin") return undefined; // check-ins are about people, never a room's health (analytics A4)
      if (templateId === "task_done" && (!taskId || !tasks.has(taskId))) return undefined;
      const k = groupOf(templateId, taskId);
      let g = groups.get(k);
      if (!g) { g = { templateId, ...(taskId ? { taskId } : {}), expected: [] }; groups.set(k, g); }
      return g;
    };
    const exp = expectedChecks(day);
    for (const e of exp) addGroup(e.templateId, e.taskId)?.expected.push(e);
    for (const r of dayEntries) if (counts(r)) addGroup(r.templateId, r.taskId);
    for (const r of history) addGroup(r.templateId, r.taskId);
    for (const [key, g] of groups) {
      const task = g.taskId ? tasks.get(g.taskId) : undefined;
      const vector = vectorOfGroup(g.templateId, g.taskId);
      const room = roomOfGroup(g.templateId, g.taskId);
      const name = task?.title ?? TEMPLATE_SUBJECT[g.templateId] ?? getTemplate(g.templateId)?.title ?? g.templateId;
      const b = task ? basisOfTask({ title: task.title, requirementIds: task.requirementIds, how: task.how }) : basisOfRequirements(getTemplate(g.templateId)?.requirementIds ?? g.expected[0]?.requirementIds ?? []);
      const mine = (r: { templateId: string; taskId?: Id }) => groupOf(r.templateId, r.taskId) === key;
      // Last checked: the newest record of this log, today or in the last HISTORY_DAYS.
      const lastRec = [...history.filter(mine), ...dayEntries.filter((r) => counts(r) && mine(r))].reduce<EntryIn | undefined>((m, r) => (!m || r.createdAt > m.createdAt ? r : m), undefined);
      const lastAt = lastRec && lastRec.createdAt <= nowIso ? lastRec.createdAt : undefined;
      const lastBy = lastRec?.createdBy ? who(lastRec.createdBy) : undefined;
      const failed = dayEntries.filter((r) => counts(r) && mine(r) && !r.evaluation.ok && !r.correctiveAction).sort((a, b2) => b2.createdAt.localeCompare(a.createdAt));
      const missing = g.expected.filter((e) => !satisfiedBy(e, dayEntries));
      const late = missing.filter((e) => isLate(e, nowIso));
      const dueSoon = missing.filter((e) => !isLate(e, nowIso) && e.dueAt <= soonIso);
      const next = missing.filter((e) => e.dueAt > nowIso).sort((a, b2) => a.dueAt.localeCompare(b2.dueAt))[0];
      // Score: checks whose grace time has passed (first-day checks only once done), on time = a record by the grace time.
      const counted = g.expected.filter((e) => (e.firstDay ? !!satisfiedBy(e, dayEntries) : e.lateAt <= nowIso));
      if (counted.length) {
        const s = score.get(vector) ?? { done: 0, of: 0 };
        s.of += counted.length;
        // On time: some record in the check's own window by its grace time (satisfiedBy picks the latest, which may be later).
        s.done += counted.filter((e) => !!satisfiedBy(e, dayEntries.filter((r) => r.createdAt <= e.lateAt))).length;
        score.set(vector, s);
      }
      const logTarget = (e?: ExpectedCheck): HealthAction["target"] => (e ? { type: "check", id: e.key } : g.taskId ? { type: "task", id: g.taskId } : { type: "template", id: g.templateId });
      const subject = TEMPLATE_SUBJECT[g.templateId] ?? name;
      const tags = [`tpl:${g.templateId}`, ...(task ? surfaceTags(seedForTask(task)?.surfaceId) : [])];
      const since = lastAt ? `not checked since ${when(lastAt)}` : "not checked today";
      if (failed.length) {
        const f = failed[0]!;
        const ruleIds = f.evaluation.violations.map((v) => v.ruleId);
        const grave = ruleIds.some((r) => URGENT_RULES[r]);
        const rules = (getTemplate(f.templateId)?.rules ?? []).filter((x) => ruleIds.includes(x.id)).map(basisOfRule);
        const fb = rules.find((x) => x.basis === "law") ?? rules.find((x) => x.basis === "osha") ?? rules[0] ?? b;
        const short = failedShort(f);
        push({
          id: `log:${key.slice(4)}`, tags, vector, room, name, status: "failed", grave, ...basisOf(fb),
          line: `${short} at ${when(f.createdAt)} · no fix written yet${failed.length > 1 ? ` · ${failed.length - 1} more` : ""}`, short, since: f.createdAt,
          ...(lastAt ? { lastCheckedAt: lastAt } : {}), ...(lastBy ? { lastCheckedBy: lastBy } : {}),
          action: { kind: "fix", label: "Add what you did", target: { type: "entry", id: f.id }, templateId: f.templateId },
        });
        continue;
      }
      if (late.length) {
        const first = late[0]!;
        push({
          id: `log:${key.slice(4)}`, tags, vector, room, name, status: "late", grave: false, ...basisOf(b),
          line: `Missed at ${clock(first.dueAt)}${late.length > 1 ? ` and ${late.length - 1} more` : ""}${lastAt ? ` · last checked ${when(lastAt)}${lastBy ? ` · ${lastBy}` : ""}` : ""}`,
          short: `${subject} ${since}`, dueAt: first.dueAt,
          ...(lastAt ? { lastCheckedAt: lastAt } : {}), ...(lastBy ? { lastCheckedBy: lastBy } : {}),
          action: { kind: "log", label: "Log it now", target: logTarget(first), templateId: g.templateId },
        });
        continue;
      }
      if (!lastAt && g.expected.length) {
        const e = dueSoon[0] ?? next ?? g.expected[0]!;
        push({
          id: `log:${key.slice(4)}`, tags, vector, room, name, status: "never", grave: false, ...basisOf(b),
          line: `Never checked${dueSoon[0] || next ? ` · due ${clock(e.dueAt)}` : ""}`, short: `${subject} never checked`, dueAt: e.dueAt,
          action: { kind: "log", label: "Log it now", target: logTarget(e), templateId: g.templateId },
        });
        continue;
      }
      if (dueSoon.length) {
        const e = dueSoon[0]!;
        push({
          id: `log:${key.slice(4)}`, tags, vector, room, name, status: "soon", grave: false, ...basisOf(b),
          line: `Due ${clock(e.dueAt)}${lastAt ? ` · last checked ${when(lastAt)}${lastBy ? ` · ${lastBy}` : ""}` : ""}`, short: `${subject} due ${clock(e.dueAt)}`, dueAt: e.dueAt,
          ...(lastAt ? { lastCheckedAt: lastAt } : {}), ...(lastBy ? { lastCheckedBy: lastBy } : {}),
          action: { kind: "log", label: "Log it now", target: logTarget(e), templateId: g.templateId },
        });
        continue;
      }
      push({
        id: `log:${key.slice(4)}`, tags, vector, room, name, status: "ok", grave: false, ...basisOf(b),
        line: [lastAt ? checkedLine(lastAt, lastBy) : "Nothing due", ...(next ? [`next ${clock(next.dueAt)}`] : [])].join(" · "), short: `${subject} OK`,
        ...(next ? { dueAt: next.dueAt } : {}), ...(lastAt ? { lastCheckedAt: lastAt } : {}), ...(lastBy ? { lastCheckedBy: lastBy } : {}),
        action: { kind: "log", label: "Log this check", target: logTarget(next), templateId: g.templateId },
      });
    }
  }

  /* 2. Places objects: their interval (objects.ts), timing only. A closed day makes nothing late (H6). */
  for (const o of objects) {
    const st = objectState(o, { events: i.events ?? [], openReports: [], supplies: [], now, ...(i.learnedHours ? { learnedHours: i.learnedHours } : {}) });
    const b = basisOfSurface(o.surfaceId);
    const name = o.count && o.count > 1 ? `${o.name} ×${o.count}` : o.name;
    const lastBy = who(st.lastBy);
    const last = st.lastAt ? { lastCheckedAt: st.lastAt, ...(lastBy ? { lastCheckedBy: lastBy } : {}) } : {};
    const soonNow = st.state === "clean" && !!st.dueAt && st.dueAt <= soonIso;
    let status: HealthStatus = st.state === "overdue" ? "late" : st.state === "due" || soonNow ? "soon" : st.state === "not_started" ? "never" : "ok";
    let line: string;
    if (status === "late" && closedToday) { status = "soon"; line = `Due when you open${st.lastAt ? ` · last done ${when(st.lastAt)}${lastBy ? ` · ${lastBy}` : ""}` : ""}`; }
    else if (status === "late") line = `Due since ${when(st.dueAt!)}${st.lastAt ? ` · last done ${when(st.lastAt)}${lastBy ? ` · ${lastBy}` : ""}` : ""}`;
    else if (status === "soon") line = `Due ${when(st.dueAt!)}${st.lastAt ? ` · last done ${when(st.lastAt)}${lastBy ? ` · ${lastBy}` : ""}` : ""}`;
    else if (status === "never") line = "Never marked done";
    else line = `Done ${when(st.lastAt!)}${lastBy ? ` · ${lastBy}` : ""} · next ${when(st.dueAt!)}`;
    push({
      id: `object:${o.id}`, tags: surfaceTags(o.surfaceId), vector: vectorOfSurface(o.surfaceId), room: o.zone, name, status, grave: false, ...basisOf(b), line,
      short: status === "late" ? `${name} due since ${when(st.dueAt!)}` : status === "never" ? `${name} never marked done` : status === "soon" ? `${name} due soon` : `${name} OK`,
      ...last, ...(st.dueAt ? { dueAt: st.dueAt } : {}),
      action: { kind: "object", label: "Clean it", target: { type: "object", id: o.id } },
    });
  }

  /* 3. Open reports (never private or health reports, except the vomit cleanup, which is a place's problem). */
  if (business) {
    for (const r of i.reports ?? []) {
      if (r.status === "resolved" || isPrivateReport(r) || (HEALTH_INCIDENT_TYPES.has(r.type) && r.type !== "vomit_diarrhea_cleanup")) continue;
      const info = incidentInfo(r.type);
      const graveCite = GRAVE_REPORT_TYPES[r.type] ?? (GRAVE_WORDS.test(`${r.title} ${r.details ?? ""}`) ? "Food Code 8-404.11" : undefined);
      const b = graveCite ? basisOfCitations([graveCite]) : basisOfRequirements(info.requirementIds, basisOfCitations([], { reason: "A problem someone reported: look and fix it." }));
      const room = rooms?.normalize(r.area) ?? objects.find((o) => { const n = lower(o.name); return n.length >= 3 && (lower(r.title).includes(n) || lower(r.details).includes(n)); })?.zone
        ?? (r.area && !/^not shared$/i.test(r.area) ? r.area.replace(/^Somewhere else:\s*/i, "") : "Other places");
      const by = who(r.reportedBy);
      push({
        id: `incident:${r.id}`, tags: [`report:${r.type}`, ...(/\bsew(age|er)\b/i.test(`${r.title} ${r.details ?? ""}`) ? ["sewage"] : [])], vector: REPORT_VECTOR[r.type] ?? "other", room, name: r.title, status: "failed", grave: !!graveCite, ...basisOf(b),
        line: `Reported ${when(r.reportedAt)}${by ? ` · ${by}` : ""} · not fixed yet`, short: r.title, since: r.reportedAt, lastCheckedAt: r.reportedAt, ...(by ? { lastCheckedBy: by } : {}),
        action: { kind: "report", label: "Open the report", target: { type: "incident", id: r.id } },
      });
    }
  }

  /* 4. Food: anything to throw out now (foodstore.ts throwOutFlag, F10), and one line for the rest. */
  const foodRoom = business ? zoneFor("reach-in-fridge") : "Kitchen";
  const food = (i.food ?? []).filter((f) => f.place !== "gone");
  let foodOk = 0;
  let foodLast: { at: string; by?: string } | undefined;
  for (const f of food) {
    if (f.updatedAt && f.updatedAt <= nowIso && (!foodLast || f.updatedAt > foodLast.at)) foodLast = { at: f.updatedAt, ...(f.updatedBy ? { by: f.updatedBy } : {}) };
    const flag = throwOutFlag(f, today, nowIso);
    if (!flag) { foodOk++; continue; }
    const fb = basisOfCitations(flag.citations);
    push({
      id: `food:${f.id}`, tags: ["food", "food:throw", ...(f.label?.length ? ["food:label"] : []), ...(f.timeControl ? ["food:tphc"] : []), ...(f.thawMethod || f.thawedOn ? ["food:thaw"] : [])], vector: "food", room: foodRoom, name: f.name, status: "failed", grave: true, ...basisOf(fb),
      line: `Throw it out: ${flag.reason.replace(/\.$/, "")}`, short: `Throw out ${f.name.toLowerCase()}`, since: flag.since.length === 10 ? `${flag.since}T00:00:00.000Z` : flag.since, ...(f.updatedAt ? { lastCheckedAt: f.updatedAt } : {}),
      action: { kind: "food", label: "Open the food", target: { type: "food", id: f.id } },
    });
  }
  if (foodOk) {
    const by = who(foodLast?.by);
    push({
      id: "food:store", tags: ["food", ...(food.some((f) => f.label?.length) ? ["food:label"] : []), ...(food.some((f) => f.timeControl) ? ["food:tphc"] : []), ...(food.some((f) => f.thawMethod || f.thawedOn) ? ["food:thaw"] : [])], vector: "food", room: foodRoom, name: business ? "Food store dates" : "Food dates", status: "ok", grave: false, ...basisOf(basisOfCitations(["Food Code 3-501.17"])),
      line: `${plural(foodOk, "food")} within date${foodLast ? ` · last change ${when(foodLast.at)}${by ? ` · ${by}` : ""}` : ""}`, short: "Food dates OK",
      ...(foodLast ? { lastCheckedAt: foodLast.at, ...(by ? { lastCheckedBy: by } : {}) } : {}),
      action: { kind: "food", label: "Open the food", target: { type: "food", id: "store" } },
    });
  }

  /* 5. Supplies: out (a problem) and below par (soon). Counted supplies only. */
  if (business) {
    for (const s of i.supplies ?? []) {
      const stock = (s as { stock?: { state?: string; at?: string; by?: string } }).stock;
      const outByWord = stock?.state === "out";
      if (s.onHand === undefined && !stock) continue;
      const out = outByWord || (s.onHand !== undefined && s.onHand <= 0);
      const low = !out && s.par !== undefined && s.onHand !== undefined && s.onHand < s.par;
      const user = objects.find((o) => objectNeeds(o).some((n) => n.supplyKey === s.key));
      const room = RESTROOM_SUPPLIES.has(s.key) ? zoneFor("restroom-sink") : user?.zone ?? zoneFor("storage-shelves");
      const name = s.name || s.key;
      const by = who(stock?.by);
      push({
        id: `supply:${s.key}`, tags: [`supply:${s.key}`], vector: "supplies", room, name, status: out ? "failed" : low ? "soon" : "ok", grave: false, ...basisOf(supplyBasis(s.key)),
        line: out ? `Out${stock?.at ? ` since ${when(stock.at)}${by ? ` · ${by}` : ""}` : ""}` : low ? `Low: ${s.onHand} of ${s.par}` : s.onHand !== undefined ? `${s.onHand} on hand${s.par !== undefined ? ` · keep ${s.par}` : ""}` : "Have it",
        short: out ? `Out of ${name.toLowerCase()}` : low ? `${name} low` : `${name} OK`, ...(out && stock?.at ? { since: stock.at } : {}),
        ...(stock?.at ? { lastCheckedAt: stock.at, ...(by ? { lastCheckedBy: by } : {}) } : {}),
        action: { kind: "order", label: out ? "Order it" : "See the order", target: { type: "supply", id: s.key } },
      });
    }
  }

  /* 6. Room checks: what the latest check found wrong and nobody fixed yet; each room's last Clear ✓ (H7). */
  const runs = (i.roomRuns ?? []).filter((r) => !r.closedAt);
  const inspected = new Map<string, { at: IsoDateTime; by?: string }>();
  const noteInspected = (roomKey: string, at: IsoDateTime, by?: string) => { const p = inspected.get(roomKey); if (at <= nowIso && (!p || at > p.at)) inspected.set(roomKey, { at, ...(by ? { by } : {}) }); };
  for (const room of i.rooms) {
    const mine = runs.filter((r) => r.room === room.key).sort((a, b) => b.startedAt.localeCompare(a.startedAt));
    for (const r of mine) if (r.clearedAt) noteInspected(room.key, r.clearedAt, who(r.clearedBy, r.clearedByName));
    const last = mine[0];
    if (!last || last.clearedAt) continue;
    const v = roomRunView(last, { now, timeZone: tz });
    for (const it of v.items) {
      if (it.status !== "todo" && it.status !== "deferred") continue;
      const by = who(undefined, it.answeredBy ?? last.startedByName);
      const at = it.answeredAt ?? last.startedAt;
      push({
        id: `roomitem:${last.id}:${it.key}`, tags: [`class:${it.class}`, ...(it.requirementId ? [`req:${it.requirementId}`] : []), ...surfaceTags(it.surfaceId), ...(it.height === "floor" ? ["floor"] : [])], vector: vectorOfClass(it.class, it.height), room: room.name, name: it.name, status: "failed", grave: false,
        ...(business ? { basis: it.basis, basisLine: it.basisLine } : { basis: HOME_BASIS.basis, basisLine: HOME_BASIS.line }),
        line: `Found not right ${when(at)}${by ? ` · ${by}` : ""} · ${it.status === "deferred" ? `waiting: ${it.deferReason ?? "not fixed"}` : "not fixed yet"}`,
        short: `${room.name}: ${it.name.toLowerCase()} not fixed`, since: at, lastCheckedAt: at, ...(by ? { lastCheckedBy: by } : {}),
        action: { kind: "room", label: "Finish the room check", target: { type: "room", id: room.key } },
      });
    }
  }
  // "Ready to open ✓" on a whole-place clean covers every room (each was Clear ✓ since the clean began).
  for (const p of i.placeRuns ?? []) if (p.readyAt && !p.closedAt) for (const room of i.rooms) noteInspected(room.key, p.readyAt, who(p.readyBy, p.readyByName));

  items.sort(compareHealth);

  /* Rooms. */
  const roomNames = [...new Set([...i.rooms.map((r) => r.name), ...items.map((x) => x.room)])];
  const roomKey = (name: string) => i.rooms.find((r) => lower(r.name) === lower(name))?.key ?? roomKeyOf(name);
  const healthRooms: HealthRoom[] = roomNames.map((name) => {
    const mine = items.filter((x) => lower(x.room) === lower(name));
    const key = roomKey(name);
    const st = worst(mine.map((x) => x.status), "never");
    const last = mine.filter((x) => x.lastCheckedAt).sort((a, b) => b.lastCheckedAt!.localeCompare(a.lastCheckedAt!))[0];
    const ins = inspected.get(key);
    const c = countOf(mine);
    const line = c.failed ? `${plural(c.failed, "thing")} to fix` : c.late ? `${c.late} late` : c.never && !c.ok ? "Never checked" : c.never ? `${c.never} never checked` : c.soon ? `${c.soon} due soon` : mine.length ? "All OK" : "Nothing recorded yet";
    return {
      key, name, status: mine.length || ins ? (mine.length ? st : "ok") : "never", word: "", tone: "go", line, counts: c,
      ...(last ? { lastCheckedAt: last.lastCheckedAt!, ...(last.lastCheckedBy ? { lastCheckedBy: last.lastCheckedBy } : {}) } : {}),
      ...(ins ? { lastInspectedAt: ins.at, ...(ins.by ? { lastInspectedBy: ins.by } : {}) } : {}),
      inspectedLine: ins ? `Last inspected: ${when(ins.at)}${ins.by ? ` · ${ins.by}` : ""}` : "Never inspected",
      items: mine,
    };
  }).map((r) => ({ ...r, word: HEALTH_WORD[r.status], tone: HEALTH_TONE[r.status] }));
  healthRooms.sort((a, b) => HEALTH_RANK[a.status] - HEALTH_RANK[b.status] || roomNames.indexOf(a.name) - roomNames.indexOf(b.name));

  /* Trends (analytics.ts checkOutcomes over the last 30 and 7 days before today), per vector's own logs. */
  const pastEntries = (i.past?.entries ?? []).filter(counts);
  const recordDays = new Set(pastEntries.map((e) => localDateMinutes(e.createdAt, tz).date));
  const firstRecord = [...recordDays].sort()[0];
  const counted = (d: IsoDate) => (day?.hours ? !!firstRecord && d >= firstRecord : recordDays.has(d));
  const formula = `${TREND_FORMULA_BASE} ${day?.hours ? "Closed days don't count; an open day with nothing written down counts as missed." : "A day with nothing written down counts as closed."}`;
  const trendOf = (key: VectorKey): HealthTrend | undefined => {
    if (!business || !i.past) return undefined;
    const exp = i.past.expected.filter((e) => vectorOfLog(e.templateId, e.taskId) === key);
    if (!exp.length) return undefined;
    const ents = pastEntries.filter((e) => vectorOfLog(e.templateId, e.taskId) === key);
    const win = (n: number): TrendWindow | null => {
      const from = addDaysIso(today, -n);
      const evs = checkOutcomes(exp, ents, counted, tz, (d) => d >= from && d < today);
      const done = evs.filter((e) => e.outcome === "on_time").length;
      return evs.length >= MIN_EVENTS ? { done, of: evs.length, pct: Math.round((done / evs.length) * 100), days: n } : null;
    };
    const last30 = win(TREND_DAYS), last7 = win(7);
    const line = last30 && last7 ? `On time: ${last30.pct}% over 30 days → ${last7.pct}% over the last 7`
      : last30 ? `On time: ${last30.pct}% over 30 days` : last7 ? `On time: ${last7.pct}% over the last 7 days` : "On time: not enough data yet";
    return { last30, last7, line, formula };
  };

  /* Vectors (H3: each from its own items only). */
  const vectors: HealthVector[] = [];
  for (const def of HEALTH_VECTORS) {
    if (!def.scopes.includes(i.scope)) continue;
    const mine = items.filter((x) => x.vector === def.key);
    if (def.onlyWhenUsed && !mine.length) continue;
    const c = countOf(mine);
    const st = worst(mine.map((x) => x.status), "never");
    const last = mine.filter((x) => x.lastCheckedAt).sort((a, b) => b.lastCheckedAt!.localeCompare(a.lastCheckedAt!))[0];
    const line = !mine.length ? "Nothing recorded yet"
      : c.failed ? `${c.failed} ${c.failed === 1 ? "needs" : "need"} a fix`
      : c.late ? `${c.late} late`
      : c.never ? `${c.never} never checked`
      : c.soon ? `${c.soon} due soon`
      : `All OK${last ? ` · last checked ${when(last.lastCheckedAt!)}` : ""}`;
    const s = score.get(def.key);
    const trend = trendOf(def.key);
    const b = vectorBasis(def, i.scope);
    const vRooms = [...new Set(mine.map((x) => x.room))].map((name) => {
      const rs = mine.filter((x) => x.room === name);
      const rst = worst(rs.map((x) => x.status));
      return { name, status: rst, word: HEALTH_WORD[rst], count: rs.filter((x) => x.status !== "ok").length };
    }).sort((a, b2) => HEALTH_RANK[a.status] - HEALTH_RANK[b2.status]);
    vectors.push({
      key: def.key, label: i.scope === "home" ? def.homeLabel ?? def.label : def.label, status: mine.length ? st : "never", word: mine.length ? HEALTH_WORD[st] : "Nothing yet",
      tone: HEALTH_TONE[mine.length ? st : "never"], line, basis: b.basis, basisLine: b.line, reads: def.reads, counts: c,
      ...(s && s.of > 0 ? { score: { done: s.done, of: s.of, pct: Math.round((s.done / s.of) * 100), formula: `${s.done} of ${s.of} checks on time today` } } : {}),
      ...(trend ? { trend } : {}), rooms: vRooms, items: mine,
    });
  }

  /* The overall line (H4): what needs you, worst first; else the calm line. */
  const needs = items.filter((x) => x.status === "failed" || x.status === "late");
  const total = countOf(items);
  let line: string;
  if (needs.length) {
    const shown = needs.slice(0, 2).map((x) => x.short);
    line = `${needs.length === 1 ? "1 thing needs" : `${needs.length} things need`} you: ${shown.join(" · ")}${needs.length > 2 ? ` · +${needs.length - 2} more` : ""}`;
  } else if (!items.length) line = "Nothing recorded yet. Start with a room check.";
  // The place's first day (set up today): everything is "never checked", so counting it reads as a scold at minute one
  // (PERSONA-REVIEW 2.3, 3.5: "Nothing late. 18 things never checked"). Say where to start instead.
  else if (total.never && i.day?.startedAt && localDateMinutes(i.day.startedAt, tz).date === today) line = FIRST_DAY_LINE;
  else if (total.never) line = `Nothing late. ${plural(total.never, "thing")} never checked.`;
  else if (total.soon) line = `All good now. ${total.soon} due soon.`;
  else line = "All good now.";
  const overall = worst(items.map((x) => x.status), "never");

  /* Last inspected anywhere, and the health department's last visit. */
  let lastInspected: LocationHealth["lastInspected"] = null;
  for (const [key, v] of inspected) {
    if (lastInspected && v.at <= lastInspected.at) continue;
    const name = i.rooms.find((r) => r.key === key)?.name ?? key;
    lastInspected = { at: v.at, ...(v.by ? { by: v.by } : {}), what: name, line: `Last inspected: ${name} ${when(v.at)}${v.by ? ` · ${v.by}` : ""}` };
  }
  const li = business ? i.location.lastInspection : undefined;
  const resultLabel = li ? INSPECTION_RESULTS.find((r) => r.key === li.result)?.label ?? li.result : "";
  const inspDate = li ? (() => { try { return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(new Date(`${li.on}T12:00:00Z`)); } catch { return li.on; } })() : "";
  const inspection = li ? { ...li, resultLabel, line: `Last health inspection: ${inspDate} · ${resultLabel}` } : null;
  const inspectionLine = !business ? "" : inspection ? inspection.line : i.canEditInspection ? "No health inspection on file. Add the last one." : "No health inspection on file yet.";
  const nextInspectionLine = !business ? "" : i.location.permitType === "mobile_food_business" ? nextInspectionTruckFor(i.location.state) ?? nextInspectionUnknownFor(i.location.state) : nextInspectionUnknownFor(i.location.state);

  /* Get inspection-ready: what needs you, then law and OSHA items never checked. */
  const readyItems = [...needs, ...items.filter((x) => x.status === "never" && (x.basis === "law" || x.basis === "osha"))];
  const ready = { line: readyItems.length ? `Get inspection-ready: ${plural(readyItems.length, "thing")} to put right first` : "Nothing to put right first. A whole-place clean keeps it that way.", itemIds: readyItems.map((x) => x.id) };
  const pic = day?.onShift.find((p) => p.isPersonInCharge);
  const form = business ? formReadiness(items, { state: i.location.state, ...(pic ? { pic: firstName(pic.name) } : {}), checkins: day?.checkins.length ?? 0, when }) : null;
  // The inspector's own form, item by item (inspector.ts): from the same rows, plus the team's certificates and posted signs.
  const ownForm = form ? inspectorFormFor({ state: i.location.state, county: i.location.county }) : null;
  const picNow = picFact(pic ? { name: firstName(pic.name), credentials: pic.onboarding?.credentials } : undefined, today);
  const inspector = form && ownForm ? inspectorView(ownForm, form.rows, items, {
    today, ...(i.credentials ? { credentials: i.credentials } : {}), ...(picNow ? { pic: picNow } : {}), ...(i.postedSigns ? { posted: i.postedSigns } : {}),
    ...(day ? { onShift: day.onShift.map((p) => ({ name: firstName(p.name), credentials: p.onboarding?.credentials ?? [] })) } : {}), ...(i.location.seats !== undefined ? { seats: i.location.seats } : {}),
  }) : null;

  return {
    locationId: i.location.id, scope: i.scope, name: i.location.name, at: nowIso, date: today,
    status: items.length ? overall : "never", word: items.length ? HEALTH_WORD[overall] : "Nothing yet", tone: HEALTH_TONE[items.length ? overall : "never"],
    line, needs, counts: total, vectors, rooms: healthRooms, lastInspected, inspection, inspectionLine, canEditInspection: business && !!i.canEditInspection,
    nextInspectionLine, ready, form, inspector, closedToday,
  };
}

/* ─────────────── alerts and the org view ─────────────── */

export interface HealthAlert {
  /** Stable per item and day of finding: dedupe on it (shift_notices.ref_id). */
  refId: string;
  itemId: string;
  vector: VectorKey;
  title: string;
  body: string;
  since: IsoDateTime;
}

/**
 * Alerts for the person in charge (H8): a vector is "Needs a fix" and an item behind it is grave, or owed by law or
 * OSHA, and still not fixed ALERT_AFTER_MIN minutes after it was found. One per item; the caller dedupes on refId.
 */
export function healthAlerts(h: LocationHealth, now: Date | IsoDateTime, when: (iso: IsoDateTime) => string, afterMin = ALERT_AFTER_MIN): HealthAlert[] {
  const nowMs = typeof now === "string" ? Date.parse(now) : now.getTime();
  const out: HealthAlert[] = [];
  for (const v of h.vectors) {
    if (v.status !== "failed") continue;
    for (const x of v.items) {
      if (x.status !== "failed" || !x.since || !(x.grave || x.basis === "law" || x.basis === "osha")) continue;
      if (nowMs - Date.parse(x.since) < afterMin * 60_000) continue;
      out.push({
        refId: `health:${x.id}@${x.since.slice(0, 16)}`, itemId: x.id, vector: v.key, since: x.since,
        title: `${v.label} needs you: ${x.short}`,
        body: `Found ${when(x.since)}. Not fixed after ${afterMin} minutes. ${x.action.label} (Health page).`,
      });
    }
  }
  return out;
}

/** One location's glance line, for the org view (owners with more than one location). */
export interface HealthSummary { locationId: Id; name: string; status: HealthStatus; word: string; tone: "stop" | "warn" | "info" | "go"; line: string; counts: HealthCounts; lastInspectedLine?: string; inspectionLine: string }
export function healthSummary(h: LocationHealth): HealthSummary {
  return { locationId: h.locationId, name: h.name, status: h.status, word: h.word, tone: h.tone, line: h.line, counts: h.counts, ...(h.lastInspected ? { lastInspectedLine: h.lastInspected.line } : {}), inspectionLine: h.inspectionLine };
}
/** Worst location first, then by name. "2 of 3 locations need you" or "All 3 locations good now". */
export function orgHealthLine(rows: readonly HealthSummary[]): string {
  const bad = rows.filter((r) => r.status === "failed" || r.status === "late").length;
  return bad ? `${bad} of ${rows.length} locations need you` : `All ${rows.length} locations: nothing late or failed`;
}
